import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';

import { useMutation, useQuery } from '@app/QueryClient';
import {
	apiAddBusinessAdmin,
	apiBusinessAdmins,
	apiDeleteBusinessAdmin,
	apiEditBusinessAdmin,
	apiShowBusinessAdmin,
} from '@app/Services/TeamMembers';
import { useQueryString } from '@app/useQueryString';
import {
	TApiBusinessAdmin,
	TApiBusinessAdminForm,
} from '@modules/TeamMembers/types';
import { apiError } from '@modules/utils/aprErrorHelper';
import { usePagination } from '@zeal/zeal-lib';

import { generatedKeys } from '../queryKeysFactory';

const { teamMembers } = generatedKeys;

export function useEditBusinessAdmin(opt?: any) {
	const queryClient = useQueryClient();
	const { t } = useTranslation('teams');
	const [parsedError, setParsedError] =
		useState<Record<keyof TApiBusinessAdminForm, string>>();

	const mutation = useMutation(apiEditBusinessAdmin, {
		...opt,
		onSuccess: (...args) => {
			queryClient.invalidateQueries({
				queryKey: teamMembers.base,
			});
			opt?.onSuccess?.(...args);
		},
		moduleName: t('admin'),
		operation: 'update',
	});

	const { mutate, error } = mutation;
	useEffect(() => {
		apiError<TApiBusinessAdminForm>(mutation.error).then((e) =>
			setParsedError(e)
		);
	}, [error]);

	return {
		...mutation,
		doEditBusinessAdmin: mutate,
		parsedError: parsedError,
	};
}

export function useAddBusinessAdmin(opt?: any) {
	const queryClient = useQueryClient();
	const { t } = useTranslation('teams');
	const [parsedError, setParsedError] =
		useState<Record<keyof TApiBusinessAdminForm, string>>();

	const mutation = useMutation(apiAddBusinessAdmin, {
		...opt,
		onSuccess: (...args) => {
			queryClient.invalidateQueries({
				queryKey: teamMembers.base,
			});
			opt?.onSuccess?.(...args);
		},
		moduleName: t('admin'),
		operation: 'add',
	});

	const { mutate, error } = mutation;

	useEffect(() => {
		apiError<TApiBusinessAdminForm>(mutation.error).then((e) =>
			setParsedError(e)
		);
	}, [error]);

	return {
		...mutation,
		doAddBusinessAdmin: mutate,
		parsedError: parsedError,
	};
}

export function useDeleteBusinessAdmin(opt?: any) {
	const { t } = useTranslation('teams');

	const mutation = useMutation(apiDeleteBusinessAdmin, {
		...opt,
		moduleName: t('admin'),
		operation: 'delete',
	});

	const { mutateAsync } = mutation;

	return {
		...mutation,
		doDeleteBusinessAdmin: mutateAsync,
	};
}

export function useBusinessAdmins(opt?: any) {
	const [queryState] = useQueryString();
	const { page } = queryState;

	const q = useQuery({
		queryFn: apiBusinessAdmins,
		staleTime: Infinity,
		enabled: opt?.roleId !== null,
		queryKey: teamMembers.businessAdmins({
			page,
			...(opt?.unassigned
				? { unassigned: opt?.unassigned }
				: opt?.roleId && { roleId: opt?.roleId }),
		}),
	});

	const pagination = usePagination(q?.data || {});

	React.useDebugValue(q);

	return {
		...q,
		...pagination,
		data: (q?.data?.data as TApiBusinessAdmin[]) || [],
		meta: q?.data,
	};
}

export function useShowBusinessAdmin() {
	const [queryState] = useQueryString();

	const q = useQuery({
		queryFn: apiShowBusinessAdmin,
		queryKey: teamMembers.businessAdmin({
			adminId: queryState?.adminId,
		}),
		staleTime: Infinity,
		enabled: !!queryState?.adminId,
	});

	React.useDebugValue(q);

	return { ...q, data: q?.data?.data as TApiBusinessAdmin | undefined };
}
